import { assign, IBasePickerSuggestionsProps, IconButton, IPersonaProps, Label, Link, NormalPeoplePicker, Persona, PersonaSize, Spinner } from '@fluentui/react';
import { Patient } from '../../api';
import * as React from 'react';
import { ROUTES } from '../../config/routes';
import { HealAPI } from '../../healApi';
import './PatientSelector.scss';

const limitedSearchAdditionalProps: IBasePickerSuggestionsProps = {
  resultsMaximumNumber: 10,
  className: 'patient-selector-suggestion-item',
};

const suggestionProps: IBasePickerSuggestionsProps = {
  suggestionsHeaderText: 'Suggested Patients',
  noResultsFoundText: 'No results found',
};

const limitedSearchSuggestionProps: IBasePickerSuggestionsProps = assign(limitedSearchAdditionalProps, suggestionProps);

export interface PatientSelectorProps {
  label?: string;
  patientId?: string;
  readonly?: boolean;
  tab?: string;
  onChange: (patient: Patient) => void;
  tid?: string;
}

export interface PatientSelectorState {
  selectedPatient?: Patient;
  editPatient?: boolean;
  isFetching?: boolean;
}

export class PatientSelector extends React.Component<PatientSelectorProps, PatientSelectorState> {
  constructor(props: PatientSelectorProps) {
    super(props);
    this.state = {};
  }

  public componentDidMount() {
    if (this.props.patientId) {
      this.getPatientDetails(this.props.patientId, '');
    }
  }

  public componentDidUpdate(prevProps: PatientSelectorProps) {
    if (
      this.props.patientId !== prevProps.patientId &&
      this.props.patientId &&
      (!this.state.selectedPatient || (this.state.selectedPatient && this.props.patientId !== this.state.selectedPatient.patientId))
    ) {
      this.getPatientDetails(this.props.patientId, '');
    }
  }

  public render(): JSX.Element {
    return (
      <div>
        {this.props.label && <Label>{this.props.label}</Label>}
        <div className="flex flex-center">
          <div style={{ display: 'inline-flex' }}>
            {this.state.isFetching ? <Spinner style={{ alignItems: 'flex-start', marginTop: 6, marginBottom: 6 }} /> : this.renderPatientSelector()}
          </div>
          {!this.props.readonly && (
            <IconButton
              iconProps={{ iconName: this.state.editPatient ? 'RevToggleKey' : 'Edit' }}
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                verticalAlign: 'middle',
                marginLeft: this.state.editPatient ? 10 : 2,
              }}
              onClick={() => this.setState({ editPatient: !this.state.editPatient })}
              data-tid={this.props.tid}
            />
          )}
        </div>
      </div>
    );
  }

  private renderPatientSelector(): JSX.Element | null {
    if (!this.state.editPatient) {
      const onRenderPrimaryText = () => {
        return (
          <Link target="_blank" href={`${ROUTES.patients}/${this.state.selectedPatient!.patientId}/${this.props.tab || 'profile'}`}>
            {this.state.selectedPatient!.fullName}
          </Link>
        );
      };
      return (
        <div style={{ marginTop: 4, marginBottom: 4 }}>
          {this.state.selectedPatient && (
            <Persona
              size={PersonaSize.size24}
              text={this.state.selectedPatient.fullName}
              onRenderPrimaryText={onRenderPrimaryText}
              imageUrl={this.state.selectedPatient.avatarUrl}
            />
          )}
        </div>
      );
    } else {
      return (
        <NormalPeoplePicker
          onResolveSuggestions={this._onFilterChanged}
          key={'controlled'}
          selectedItems={this.formatPatientDataForPicker([this.state.selectedPatient])}
          pickerSuggestionsProps={limitedSearchSuggestionProps}
          onChange={this._onItemsChange}
          resolveDelay={300}
          itemLimit={2}
        />
      );
    }
  }

  private _onFilterChanged = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return HealAPI.searchPatient(filterText).then((response) => {
        if (!response.ok || !response.data) {
          return [];
        } else {
          return this.formatPatientDataForPicker(response.data);
        }
      });
    }
    return [];
  };

  private _onItemsChange = (items: any): void => {
    if (items.length > 0) {
      this.getPatientDetails(items[0].patientId, items[0].tertiaryText);
    } else {
      this.setState({ selectedPatient: undefined });
    }
  };

  private formatPatientDataForPicker(patients: any[]): IPersonaProps[] {
    return (patients || [])
      .filter((i) => !!i)
      .map((p) => ({
        styles: {
          root: { height: '100%' },
          primaryText: { fontSize: '13px', textDecoration: 'none', fontWeight: '600' },
          secondaryText: { fontSize: '12px' },
          tertiaryText: { fontSize: '12px' },
        },
        size: PersonaSize.size72,
        coinSize: 50,
        text: p.fullName,
        secondaryText: 'Patient: ' + p.email,
        tertiaryText: p.selectedUserAccountEmail || 'User: ' + p.userAccountEmail,
        imageUrl: p.avatarUrl,
        patientId: p.patientId,
      }));
  }

  private getPatientDetails(patientId: string, selectedUserAccountEmail: string): void {
    this.setState({ isFetching: true });
    HealAPI.fetchPatient(patientId).then((response) => {
      if (response.ok && response.data) {
        const selectedPatient = response.data.data;
        if (this.props.onChange) {
          this.props.onChange(selectedPatient);
        }
        this.setState({
          selectedPatient: { ...response.data.data, selectedUserAccountEmail: selectedUserAccountEmail },
          editPatient: false,
          isFetching: false,
        });
      } else {
        this.setState({ selectedPatient: undefined, isFetching: false });
      }
    });
  }
}
